import jwtDecode from 'jwt-decode'

import { retry } from './helpers'
import api from './resources'
import storage from './storage'

import { transform, timestamp } from 'js/vuex/helpers'

/**
 * Stores the current authentication request Promise
 * @type Promise
 */
var authenticating = null;

/**
 * Store the current access token refresh timer
 * @type {[type]}
 */
var authTimer = null;


/**
 * Used to initialize the authentication refresh process
 */
export function checkAuthentication() {

    return authenticate().then( () => {
        if ( authTimer == null ) {
            updateRefreshTimer()
        }
        return getUserId()
    })

}

/**
 * Check user authorization for the given route
 * @param  {Vue-route object} route
 * @param  {User object} user
 * @param  {bool} adminRequired
 * @return Boolean
 */
export function checkAuthorization(route, user, adminRequired) {

    if ( user == null ) {
        return false
    }

    if ( isAdmin(user) ) {
        return true
    } else if ( adminRequired ) {
        return false
    }

    if ( route.params.groupId != null && user.groups.indexOf(route.params.groupId) < 0 ) {
        // no permission to view this group
        return false
    }

    if ( canTeach(user) && route.name.split('.')[0] === 'student' ) {
        return false
    }

    return true

}

export function canTeach(user) {
    return isTeacher(user) || isDemo(user) || isAdmin(user)
}

export function isTeacher(user) {
    return user.role === 'teacher'
}

export function isDemo(user) {
    return user.role === 'demo'
}

export function isStudent(user) {
    return user.role === 'student' || user.role === 'demostudent'
}

export function isAdmin(user) {
    return user.role === 'admin'
}

/**
 * Gets the currently logged in user id
 * @return Int
 */
export function getUserId() {
    const tokenString = getAccessToken() || getRefreshToken()
    if ( tokenString == null ) {
        return null
    }

    const token = jwtDecode(tokenString)
    return token.sub

}

/**
 * Stores the given tokens
 * @param  [TokenAPIData] tokens
 */
export function receivedTokens(tokens) {

    for( const token of tokens ) {

        const jwtToken = jwtDecode(token.id)

        // check if the token is access or refresh
        if ( 'rfc' in jwtToken) {
            setAccessToken(token.id)
            updateRefreshTimer()
        } else {
            setRefreshToken(token.id)
        }
    }

}

/**
 * Login with the given credentials
 * @param  {String} email
 * @param  {String} password
 * @param  {Boolean} remember true if a 30-day refresh token should be received
 * @return {Promise}
 */
export function login(email, password, remember) {

    // make sure there are no old tokens lying around
    removeTokens()

    return api.login(email, password, remember)
        .then( response => transform(response.body) )
        .then( result => {
            if ( result.tokens ) {
                receivedTokens(result.tokens)
            }
        })

}


/**
 * Blacklists and destroys currently available tokens
 * @return Resolving Promise
 */
export function logout() {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()

    clearTimeout(authTimer)

    if ( accessToken != null || refreshToken != null ) {
        return api.logout(refreshToken)
            .catch( () => {} )
            .then( () => removeTokens())
    }

    return Promise.resolve()

}


/**
 * Get the access token from storage
 * @return String
 */
export function getAccessToken() {
    return storage.local.getItem('accessToken')
}

/**
 * Store the access token to storage
 */
function setAccessToken(token) {
    storage.local.setItem('accessToken', token)
    storage.local.setItem('accessTokenReceived', timestamp())
}

/**
 * Get the timestamp when we stored the last access token
 * @return String timestamp
 */
function getLastAccessTokenReceived() {
    return storage.local.getItem('accessTokenReceived')
}

/**
 * Get the refresh token from storage
 * @return String
 */
function getRefreshToken() {
    return storage.local.getItem('refreshToken')
}

/**
 * Store the refresh token to storage
 */
function setRefreshToken(token) {
    storage.local.setItem('refreshToken', token)
}

/**
 * Removes all token data from storage
 */
function removeTokens() {
    storage.local.removeItem('accessToken')
    storage.local.removeItem('refreshToken')
    storage.local.removeItem('accessTokenReceived')
}




/**
 * Helper to add an authorization header to a request
 * @param Request
 */
export function setAuthorizationHeader(request) {

    const accessToken = getAccessToken()
    if ( !request.headers.has('Authorization') && accessToken !== null) {
        request.headers.set('Authorization', 'Bearer ' + accessToken)
    }

}


/**
 * Checks the current tokens if the user can login
 * @return Promise
 */
function authenticate() {

    const accessToken = getAccessToken()

    if ( accessToken != null ) {

        const remaining = timeRemaining(accessToken)
        if ( remaining >= -5 ) {
            // There is an access token available, not guaranteed to be valid
            return Promise.resolve()
        }

    }

    return singletonGetAccessTokenWithRefreshToken()

}


/**
 * Retries the given request after authentication
 * @param  request
 * @return Promise
 */
export function retryAfterAuthFail(request) {

    console.log('Retry after auth fail', request)

    // Login requests cannot be retried, wrong credentials is wrong credentials
    // Refresh request cannot be retried, because it would cause a deadlock (promise returning itself on failure), unless it's stealthy (i.e. called by timer)
    if ( request && (request.url === 'auth/login' || request.url === 'auth/refresh' )) {
        return request.stealth ? singletonGetAccessTokenWithRefreshToken() : Promise.reject('Auth requests cannot be retried')
    }

    return singletonGetAccessTokenWithRefreshToken()
        .then( () => retry(request) )

}



/**
 * Calculates the duration for which the given token is valid
 * @param  JwtToken
 * @return Int duration in seconds
 */
function timeValid(token) {
    const timeValid = token.exp - token.iat
    return (timeValid - 60) // trim 60 seconds to be safe
}

/**
 * Calculates the remaining time an access token is valid
 * @param  String token
 * @return Int remaining seconds
 */
function timeRemaining(token) {

    const jwtToken = jwtDecode(token)
    const tokenReceivedAt = getLastAccessTokenReceived()
    const validFor = timeValid(jwtToken)

    if ( tokenReceivedAt == null ) {
        return validFor
    }

    const receivedAt = parseInt(tokenReceivedAt, 10)
    const currentTime = timestamp()
    return receivedAt + validFor - currentTime

}

/**
 * Updates the refresh timer based on the stored access token
 */
function updateRefreshTimer() {

    const token = getAccessToken()
    const timeout = timeRemaining(token)

    console.log('Setting new refresh timer', timeout);

    clearTimeout(authTimer)
    authTimer = setTimeout( () => {

        const stealth = true
        refresh(token, stealth).catch( () => console.error('Could not refresh token') )
        authTimer = null

    }, timeout * 1000)

}

/**
 * Tries to auto authenticate if a refresh token is available
 * If there is already an auto authenticate request running, that's returned instead
 * @return Promise
 */
function singletonGetAccessTokenWithRefreshToken() {

    // Check if another request is already authenticating
    if ( authenticating != null ) {

        console.log('Waiting for previous authentication');
        return authenticating

    }

    authenticating = getAccessTokenWithRefreshToken()

    authenticating.catch(() => {}).then( () => { authenticating = null })

    return authenticating

}


/**
 * Requests a new Access Token with the stored Refresh Token (if exists)
 * @return Promise
 */
function getAccessTokenWithRefreshToken() {

    const refreshToken = getRefreshToken()
    if ( refreshToken == null ) {
        return Promise.reject('Refresh token not found')
    }

    console.log('Found refresh token...');
    return refresh( refreshToken )

}

/**
 * Gets a new Access Token, authenticated with the given token (which must be still valid)
 * @param  token  a valid token to authenticate (can be access or refresh)
 * @param  boolean stealth  true if the refresh api call is a stealthy access token request by automatic timer
 * @return Promise
 */
function refresh(token, stealth) {

    return api.getAccessToken(token, stealth)
        .then( response => {

            const result = transform(response.body)
            receivedTokens(result.tokens)

            return response

        })

}



// WEBPACK FOOTER //
// ./resources/assets/js/api/auth.js